import { addCalendarDays, fromCalendarDate, isCalendarDate } from "@repo/utils/date";
import { z } from "zod";
import { calendarDateSchema, isoDateTimeSchema } from "./dates";
import {
  addDocumentValidity,
  getDocumentValidityEntry,
  hasComputableValidity,
} from "./document-validity";

export const documentDateInputSchema = z
  .object({
    date: calendarDateSchema,
    expectedUpdatedAt: isoDateTimeSchema,
  })
  .strict();
export type DocumentDateInput = z.infer<typeof documentDateInputSchema>;

/** Stored under DocumentVersion.metadata.validityReview. Independent of the form UI. */
export const documentDateReviewSchema = z.object({
  date: calendarDateSchema,
  expiresAt: isoDateTimeSchema,
  actor: z.string().min(1),
  source: z.enum(["ADMIN", "SIMULATION"]),
  recordedAt: isoDateTimeSchema,
});
export const documentDateHistorySchema = z.array(documentDateReviewSchema);
export type DocumentDateReview = z.infer<typeof documentDateReviewSchema>;

/** A document stays valid through its last day and expires when the next calendar day starts. */
export function expiryFromDocumentDate(name: string, date: string): Date | null {
  const rule = getDocumentValidityEntry(name);
  if (!hasComputableValidity(rule) || !isCalendarDate(date)) return null;
  const lastDay = rule.dateType === "issuedAt" ? addDocumentValidity(rule, date) : date;
  return fromCalendarDate(addCalendarDays(lastDay, 1));
}

export const documentSimulationInputSchema = z
  .object({
    daysRemaining: z.number().int().min(-365).max(3650),
    expectedUpdatedAt: isoDateTimeSchema,
    replay: z.boolean().default(false),
  })
  .strict();
export type DocumentSimulationInput = z.infer<typeof documentSimulationInputSchema>;

export function readDocumentDateReview(metadata: unknown): DocumentDateReview | null {
  return (
    z.object({ validityReview: documentDateReviewSchema }).safeParse(metadata).data
      ?.validityReview ?? null
  );
}

export const documentLifecycleViewSchema = z.object({
  id: z.number(),
  name: z.string(),
  versionStatus: z.string(),
  isCurrent: z.boolean(),
  updatedAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema.nullable(),
  suggestedDate: calendarDateSchema.nullable(),
  review: documentDateReviewSchema.nullable(),
  history: documentDateHistorySchema,
  approvedVersion: z.object({ id: z.number(), expiresAt: isoDateTimeSchema.nullable() }).nullable(),
  driverPaused: z.boolean(),
  driverAccepted: z.boolean(),
  events: z.array(
    z.object({
      id: z.number(),
      event: z.string(),
      attempts: z.number(),
      processedAt: isoDateTimeSchema.nullable(),
      lastError: z.string().nullable(),
      deliveries: z.array(z.object({ channel: z.string(), status: z.string() })),
    })
  ),
});
export type DocumentLifecycleView = z.infer<typeof documentLifecycleViewSchema>;
